<template>
  <v-container fluid v-if="show">
    <v-dialog v-model="show" max-width="400px">
      <v-card>
        <v-card-title class="secondary white--text d-flex align-center justify-space-between">
          <span class="header-font" style="font-size: 22px;">
            <v-icon style="font-size: 18px;" color="white" class="mr-2">share</v-icon>
            {{ $t(title) }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-tabs grow color="secondary" class="elevation-0 my-3 monospace-font" v-model="currentTab" height="35" @change="onTypeChange()">
            <v-tab>{{ $t('share.typeInternal') }}</v-tab>
            <v-tab>{{ $t('share.typeExternal') }}</v-tab>
          </v-tabs>
          <!--Only Hub warning-->
          <v-alert dense type="warning" :border="'left'" class="my-2 py-1 body-2 font-weight-medium"
            v-if="currentTab === 0 && (!isOldClient && !userHasRole('ROLE_DRIBLAB_HUB') && !userHasRole('ROLE_ADMIN'))">
            {{ $t('hub.onlyHub') }}
          </v-alert>
          <v-form name="form" ref="formRef" @submit.prevent="onSaveClick">
            <p class="mb-2 mt-5 body-1 font-weight-bold primary--text">{{ $t('share.shareTo') }}</p>
            <!--Internal-->
            <div v-if="currentTab === 0">
              <v-autocomplete clearable hide-details dense outlined class="slim-input"
                :disabled="!isOldClient && !userHasRole('ROLE_DRIBLAB_HUB') && !userHasRole('ROLE_ADMIN')"
                :rules="requiredRules" 
                :items="users" 
                item-value="id" item-text="label"
                :placeholder="$t('share.selectUser')"
                v-model="share.shareToId">
              </v-autocomplete>
              <!--Via-->
              <p class="mb-0 mt-5 body-1 font-weight-bold primary--text">Via</p>
              <div class="d-flex">
                <v-checkbox v-for="via of vias" :key="via.label" color="secondary"
                  :disabled="!isOldClient && !userHasRole('ROLE_DRIBLAB_HUB') && !userHasRole('ROLE_ADMIN')"
                  multiple hide-details class="my-0 me-5"
                  :rules="requiredLengthRules"
                  v-model="share.viaModel"
                  :value="via.value"
                >
                  <template v-slot:label>
                    <span class="body-1 primary--text">{{ via.label }}</span>
                  </template>
                </v-checkbox>
              </div>
            </div>
            <!--External-->
            <div v-if="currentTab === 1"  @keydown.enter.prevent="onAddExt()">
               <v-text-field outlined dense hide-details 
                  :placeholder="$t('share.viaEmail')"
                  v-model="share.shareToEmail" 
                  :rules="emailRules"
                  @keydown.enter="onAddExt()"
                  class="mb-2 slim-input"
                ></v-text-field>     
              <div class="d-flex align-center flex-wrap mb-2">
                <v-chip class="mr-1 mt-1 body-1" small color="secondary" close @click:close="onRemoveExt(index)" v-for="(ext,index) of share.shareToExtList" :key="index">
                  {{ext.mail}}
                </v-chip>
              </div>
            </div>
            <!--Description-->
            <p class="mb-2 mt-5 body-1 font-weight-bold primary--text">{{ $t('share.description') }}</p>
            <v-textarea outlined hide-details :placeholder="$t('reports.writeYourMessage')" 
              v-model="share.descriptionModel"
              :disabled="currentTab === 0 && (!userHasRole('ROLE_DRIBLAB_HUB') || !userHasRole('ROLE_ADMIN'))"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions class="pr-6 pb-4 d-flex justify-end">
          <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary'" rounded outlined class="elevation-0 monospace-font px-4" @click="show = false">{{$t('general.cancel')}}</v-btn>
          <v-btn rounded class="elevation-0 monospace-font white--text px-4" color="primary" @click="onSaveClick()">{{$t('general.share')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

import * as _ from 'lodash';
import ClientService from '../../services/client.service';
import { userHasRole } from '../../utils/functions';
import moment from 'moment';

export default {
  name: 'ShareFormModal',
  components: {
  },
  filters: {
  },
  props:{
    title:{
      type:String,
      default: "general.share"
    },
    external:{
      type:Boolean,
      default: false
    },
    videoShareType:{
      type:String,
      default: null
    },
  },
  computed:{
    currentUser() {
      return this.$store.state.auth.user;
    },
    requiredRules(){
      return [
        v =>
          !!v || this.$t("general.formErrors.required")
      ]
    },
    emailRules(){
      return [
        v =>
          (this.share.shareToExtList.length > 0 ? true : !!v) ||
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,20})+$/.test(v) ||
          "Email must be valid"
      ]
    },
    requiredLengthRules(){
      return [
        v =>
          v.length > 0 || this.$t("general.formErrors.required")
      ]
    },
    isOldClient() {
      const clientStartMoment = moment(this.currentUser.startDate);
      const limitDate = moment('2024-01-01');
      return clientStartMoment.isBefore(limitDate);
    },
  },
  data() {
    return {
      share: null,
      show: false,
      users: [],
      currentTab: 0,
      vias: [ { value: 'app', label: this.$t('app.inbox') }, { value: 'email', label: this.$t('share.viaEmail') }]
    };
  },
  async created() {
    await this.loadUsers();
    document.addEventListener("keydown",this.onKeyDown)
  },
  methods: {
    open(share = { typeModel: 'internal', viaModel: ['app', 'email'] }) {
      if (this.external) {
        share.typeModel = 'external';
        this.currentTab = this.external ? 1 : 0;
      } else {
        share.typeModel = 'internal';
        this.currentTab = 0;
      };
      share.shareToExtList = [];
      this.share = share;
      this.show = true;
    },
    close(){
      this.share = null;
      this.show = false;
    },
    async onSaveClick(){
      if (this.$refs.formRef.validate()) {
        if (this.currentTab === 1) {
          this.share.viaModel = ['email'];
          this.share.typeModel = 'external';
          this.onAddExt();
        } else this.share.typeModel = 'internal';

        if (this.videoShareType) this.share.videoShareType = this.videoShareType;
        
        this.$emit('save', this.share);
        this.close();
      };
    },
    async loadUsers(){
      let response = await ClientService.getUsersOfSomeClient(true);
      this.users = _.chain(response.data).map(u => {
        u.label = `${u.firstName} ${u.lastName} (${u.email})`
        return u;
      }).orderBy('label','asc').value();
    },
    onTypeChange(){
      if (this.share.typeModel === 'external') {
        this.share.viaModel = ['app', 'email'];
      };
    },
    userHasRole(role) {
      return userHasRole(this.currentUser, role);
    },
    onAddExt(){
      if(this.share.shareToEmail){
        this.share.shareToExtList.push({mail:this.share.shareToEmail});
        this.share.shareToEmail = "";
      }
    },
    onRemoveExt(index){
      this.share.shareToExtList.splice(index,1);
    }
  },
  destroyed(){
    document.removeEventListener("keydown",this.onKeyDown)
  }
};
</script>



// WEBPACK FOOTER //
// src/views/base/ShareFormModal.vue